// @flow
import React, { useContext } from 'react';
import classnames from 'classnames';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { t } from 'Src/i18n';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';

import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton.jsx';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { ProductMustTryCard } from 'Components/widgets/product-must-try-card/ProductMustTryCard.jsx';
import { SliderHandler } from 'Components/frameworks/slider-handler/SliderHandler.jsx';

type Props = {
  className?: string,
  isFrench?: boolean,
  items: Array<{
    description: string,
    id: string,
    imageSrc: string,
    seeMoreLabel?: string,
    title: string,
    trackingData?: TrackingData,
  }>,
  productColor: Colors,
  productIconName: IconName,
};

export function ProductMustTryLarge(props: Props) {
  const { className, isFrench, items = [], productColor, productIconName } = props;

  const { isDesktop } = useContext(ResponsiveContext);

  return (
    <div className={classnames(className, 'relative -mx-5 overflow-hidden')}>
      <div
        className={`absolute bg-${productColor} flex justify-center overflow-hidden p-5 top-0 rounded-tiny w-full`}
        style={{ height: '24.375rem' }}
      >
        <div className={classnames('relative px-15', { 'pt-3': isFrench })}>
          <div className="absolute opacity-25 top-0 rtl:left-0">
            <Icon color={COLORS.BLACK} isNotSquare name={productIconName} width="8.75rem" />
          </div>
          <Icon
            color={COLORS.WHITE}
            name={isFrench ? ICONS.MUST_TRY_EXPERIENCES_FR : ICONS.MUST_TRY_EXPERIENCES}
            width={isFrench ? '11rem' : '10rem'}
          />
        </div>
      </div>
      <SliderHandler
        className="-mx-2"
        hasKeyboardSupport
        hasLazyload
        hasSwipeSupport
        items={items.map(item => (
          <div className="px-2 relative h-full" key={item.title}>
            <ProductMustTryCard {...item} />
          </div>
        ))}
        nbItemsToScroll={1}
        nbItemsToShow={isDesktop ? 4 : 3}
      >
        {({
          hasControls,
          isNextControlDisabled,
          isPreviousControlDisabled,
          itemsNode,
          onNext,
          onPrevious,
        }) => (
          <div className="relative mt-24 mx-4">
            {itemsNode}
            {hasControls && (
              <div className="absolute flex items-center justify-between inset-0 pointer-events-none w-full">
                {!isPreviousControlDisabled && (
                  <ArrowButton
                    className="pointer-events-auto rtl:-ml-2"
                    colorBg={COLORS.WHITE}
                    color={COLORS.GRAY_DARKER}
                    label={t('common.previous')}
                    onClick={onPrevious}
                  />
                )}
                {!isNextControlDisabled && (
                  <ArrowButton
                    className="pointer-events-auto rtl:-mr-2 rtl:ml-auto"
                    colorBg={COLORS.WHITE}
                    color={COLORS.GRAY_DARKER}
                    label={t('common.next')}
                    onClick={onNext}
                    rotation={180}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </SliderHandler>
    </div>
  );
}
